import { useParamsStore } from '@/store/local-storage/params';
import type { IHttpResponse } from '@/store/definition';

export interface ICloudCommonApi {
  // 件名情報取得
  getProject(idToken: string, projectId: string, errorCallback: any): Promise<IHttpResponse>;
  // ユーザー情報取得
  getUser(
    idToken: string,
    projectId: string,
    userId: string,
    errorCallback: any
  ): Promise<IHttpResponse>;

  // コントローラグループ一覧取得
  getControllerGroups(queryParam: any, errorCallback: any): Promise<IHttpResponse>;

  //コントローラグループ詳細取得
  getControllerGroup(id: string, queryParam: any, errorCallback: any): Promise<IHttpResponse>;

  //コントローラ詳細取得
  getController(id: string, queryParam: any, errorCallback: any): Promise<IHttpResponse>;

  //信号変換器一覧取得
  getControllerEmits(id: string, queryParam: any, errorCallback: any): Promise<IHttpResponse>;
  //照明器具一覧取得
  getControllerEmitLamps(
    controllerId: string,
    emitId: string,
    queryParam: any,
    errorCallback: any
  ): Promise<IHttpResponse>;
}

const getCloudCommonHeaderParams = () => {
  const params = useParamsStore();
  const { $getEnvName, $ENV_NAME, $CLOUD_COMMON_SERVER_CONFIG } = useNuxtApp();
  const config = useRuntimeConfig();
  let envName = $getEnvName();
  if (envName === $ENV_NAME.LOCAL && config.public.appEnv) {
    envName = config.public.appEnv;
  }
  const headers: any = {};
  // 環境に応じてx-api-keyを設定
  let apiKey = '';
  switch (envName) {
    case $ENV_NAME.LOCAL:
      apiKey = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.LOCAL_API_KEY;
      break;

    case $ENV_NAME.DEV:
      apiKey = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.DEV_API_KEY;
      break;

    case $ENV_NAME.TEST:
      apiKey = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.TEST_API_KEY;
      break;

    case $ENV_NAME.STG:
      apiKey = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.STG_API_KEY;
      break;

    case $ENV_NAME.PROD:
      apiKey = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.PROD_API_KEY;
      break;

    default:
      apiKey = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.LOCAL_API_KEY;
      break;
  }
  headers['x-api-key'] = apiKey;

  headers['Authorization'] = params.idToken;
  return headers;
};

class CloudCommonApi implements ICloudCommonApi {
  /**
   * 件名情報取得API
   * @param projectId
   * @param errorCallback
   * @returns
   */
  async getProject(projectId: string, errorCallback: any): Promise<IHttpResponse> {
    const { $CLOUD_COMMON_SERVER_CONFIG, $ApiWrapper, $getCloudCommonApiUrl } = useNuxtApp();

    const url =
      $getCloudCommonApiUrl() +
      $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_API_NAME.GET_PROJECT.replace(
        '{projectId}',
        projectId
      );

    const maxRetryCount = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.MAX_RETRY_COUNT;
    const headersParam = getCloudCommonHeaderParams();

    const response: Promise<IHttpResponse> = $ApiWrapper.get(
      url,
      headersParam,
      {},
      maxRetryCount,
      errorCallback
    );

    return response;
  }

  /**
   * ユーザー情報取得API
   * @param idToken
   * @param projectId
   * @param userId
   * @param errorCallback
   * @returns
   */
  async getUser(projectId: string, userId: string, errorCallback: any): Promise<IHttpResponse> {
    const { $CLOUD_COMMON_SERVER_CONFIG, $ApiWrapper, $getCloudCommonApiUrl } = useNuxtApp();

    const url =
      $getCloudCommonApiUrl() +
      $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_API_NAME.GET_USER.replace(
        '{projectId}',
        projectId
      ).replace('{userId}', userId);

    const maxRetryCount = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.MAX_RETRY_COUNT;
    const headersParam = getCloudCommonHeaderParams();

    const response: Promise<IHttpResponse> = $ApiWrapper.get(
      url,
      headersParam,
      {},
      maxRetryCount,
      errorCallback
    );

    return response;
  }

  /**
   * コントローラグループ一覧取得
   *
   * @param queryParam
   * @param errorCallback
   * @returns
   */
  async getControllerGroups(queryParam: any, errorCallback: any): Promise<IHttpResponse> {
    const { $CLOUD_COMMON_SERVER_CONFIG, $ApiWrapper, $getCloudCommonApiUrl } = useNuxtApp();
    const params = useParamsStore();

    const url =
      $getCloudCommonApiUrl() + $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_API_NAME.CONTROLLER_GROUPS;

    const maxRetryCount = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.MAX_RETRY_COUNT;
    const headersParam = getCloudCommonHeaderParams();

    queryParam['project'] = params.projectId;

    const response: Promise<IHttpResponse> = $ApiWrapper.get(
      url,
      headersParam,
      queryParam,
      maxRetryCount,
      errorCallback
    );

    return response;
  }

  /**
   * コントローラグループ詳細取得
   *
   * @param id
   * @param queryParam
   * @param errorCallback
   * @returns
   */
  getControllerGroup(id: string, queryParam: any, errorCallback: any): Promise<IHttpResponse> {
    const { $CLOUD_COMMON_SERVER_CONFIG, $ApiWrapper, $getCloudCommonApiUrl } = useNuxtApp();
    const params = useParamsStore();

    const url =
      $getCloudCommonApiUrl() +
      $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_API_NAME.CONTROLLER_GROUP.replace('{id}', id);

    const maxRetryCount = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.MAX_RETRY_COUNT;
    const headersParam = getCloudCommonHeaderParams();

    queryParam['project'] = params.projectId;

    const response: Promise<IHttpResponse> = $ApiWrapper.get(
      url,
      headersParam,
      queryParam,
      maxRetryCount,
      errorCallback
    );

    return response;
  }

  /**
   * コントローラ詳細取得
   *
   * @param id
   * @param queryParam
   * @param errorCallback
   * @returns
   */
  getController(id: string, queryParam: any, errorCallback: any): Promise<IHttpResponse> {
    const { $CLOUD_COMMON_SERVER_CONFIG, $ApiWrapper, $getCloudCommonApiUrl } = useNuxtApp();
    const params = useParamsStore();

    const url =
      $getCloudCommonApiUrl() +
      $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_API_NAME.CONTROLLER.replace('{id}', id);

    const maxRetryCount = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.MAX_RETRY_COUNT;
    const headersParam = getCloudCommonHeaderParams();

    queryParam['project'] = params.projectId;

    const response: Promise<IHttpResponse> = $ApiWrapper.get(
      url,
      headersParam,
      queryParam,
      maxRetryCount,
      errorCallback
    );

    return response;
  }
  /**
   * 信号変換器一覧取得
   *
   * @param id
   * @param queryParam
   * @param errorCallback
   * @returns
   */
  getControllerEmits(id: string, queryParam: any, errorCallback: any): Promise<IHttpResponse> {
    const { $CLOUD_COMMON_SERVER_CONFIG, $ApiWrapper, $getCloudCommonApiUrl } = useNuxtApp();
    const params = useParamsStore();

    const url =
      $getCloudCommonApiUrl() +
      $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_API_NAME.CONTROLLER_EMITS.replace('{id}', id);

    const maxRetryCount = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.MAX_RETRY_COUNT;
    const headersParam = getCloudCommonHeaderParams();

    queryParam['project'] = params.projectId;

    const response: Promise<IHttpResponse> = $ApiWrapper.get(
      url,
      headersParam,
      queryParam,
      maxRetryCount,
      errorCallback
    );

    return response;
  }

  /**
   * 照明器具一覧取得
   *
   * @param controllerId
   * @param emitId
   * @param queryParam
   * @param errorCallback
   * @returns
   */
  getControllerEmitLamps(
    controllerId: string,
    emitId: string,
    queryParam: any,
    errorCallback: any
  ): Promise<IHttpResponse> {
    const { $CLOUD_COMMON_SERVER_CONFIG, $ApiWrapper, $getCloudCommonApiUrl } = useNuxtApp();
    const params = useParamsStore();

    const url =
      $getCloudCommonApiUrl() +
      $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_API_NAME.CONTROLLER_EMIT_LAMPS.replace(
        '{controllerId}',
        controllerId
      ).replace('{emitId}', emitId);

    const maxRetryCount = $CLOUD_COMMON_SERVER_CONFIG.CLOUD_COMMON_SERVER.MAX_RETRY_COUNT;
    const headersParam = getCloudCommonHeaderParams();

    queryParam['project'] = params.projectId;

    const response: Promise<IHttpResponse> = $ApiWrapper.get(
      url,
      headersParam,
      queryParam,
      maxRetryCount,
      errorCallback
    );

    return response;
  }
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      cloudCommonApi: new CloudCommonApi(),
    },
  };
});
